import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const GROUPS = ["Construction", "Labour", "Land", "Government", "Office", "Marketing", "Employee", "Vehicle", "Financial", "Other"];

export default function ExpenseCategoriesPage() {
  const qc = useQueryClient();
  const { data: cats = [] } = useQuery({ queryKey: ["expense-categories"], queryFn: async () => (await api.get("/expense-categories")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", group: "Construction", active: true });
  const [editId, setEditId] = useState(null);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of cats) { g[c.group] = g[c.group] || []; g[c.group].push(c); }
    return g;
  }, [cats]);

  function openNew() { setForm({ name: "", group: "Construction", active: true }); setEditId(null); setOpen(true); }
  function openEdit(c) { setForm({ name: c.name, group: c.group, active: c.active }); setEditId(c.id); setOpen(true); }

  async function save() {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) await api.patch(`/expense-categories/${editId}`, form);
      else await api.post("/expense-categories", form);
      toast.success(editId ? "Updated" : "Added");
      qc.invalidateQueries({ queryKey: ["expense-categories"] });
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function toggle(c) {
    try { await api.patch(`/expense-categories/${c.id}`, { active: !c.active }); qc.invalidateQueries({ queryKey: ["expense-categories"] }); }
    catch { toast.error("Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="expense-categories-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Masters</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Expense Categories</h1>
          <p className="text-sm text-zinc-500 mt-1">Add, edit or deactivate any expense head at any time. All ledgers pick from this list.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-category-btn"><Plus size={14} className="mr-1" /> New Category</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Category" : "New Category"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" /></div>
              <div><Label>Group</Label>
                <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
                  <SelectTrigger data-testid="cat-group"><SelectValue /></SelectTrigger>
                  <SelectContent>{GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="act" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
                <Label htmlFor="act">Active</Label>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-cat">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Object.entries(grouped).map(([grp, items]) => (
          <Card key={grp} className="bg-white border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading text-lg font-semibold">{grp}</div>
                <span className="text-xs text-zinc-500 font-mono">{items.length}</span>
              </div>
              <div className="space-y-1">
                {items.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 py-1.5 border-b border-zinc-100 last:border-0">
                    <Checkbox checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`cat-toggle-${c.id}`} />
                    <div className={`flex-1 text-sm ${c.active ? "" : "line-through text-zinc-400"}`}>{c.name}</div>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`cat-edit-${c.id}`}><Pencil size={12} /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
